import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaStar, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import API_URL from '../api/baseURL';

// Render a row of up to 5 stars
const StarRow = ({ value, size = 18 }) => {
  const rounded = Math.round(value);
  return (
    <span className="flex items-center gap-0.5">
      {[...Array(5)].map((_, i) => (
        <FaStar
          key={i}
          className={i < rounded ? "text-yellow-400" : "text-gray-500"}
          size={size}
        />
      ))}
      <span className="ml-2 font-bold text-yellow-300">{value.toFixed(1)}</span>
    </span>
  );
};

const WorkshopRatings = () => {
  const [ratings, setRatings] = useState([]);
  const [expandedIdx, setExpandedIdx] = useState(null); // Which workshop is expanded
  const [reviews, setReviews] = useState({});
  const [loadingReviews, setLoadingReviews] = useState({});
  const { token } = useAuth();

  useEffect(() => {
    axios.get(`${API_URL}/workshop-ratings`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => setRatings(res.data))
      .catch(err => {
        setRatings([]);
        console.error("❌ Failed to load ratings:", err);
      });
  }, [token]);

  const handleExpand = async (workshopId, idx) => {
    if (expandedIdx === idx) {
      setExpandedIdx(null); // collapse if already expanded
      return;
    }
    setExpandedIdx(idx);

    if (!reviews[workshopId]) {
      setLoadingReviews(prev => ({ ...prev, [workshopId]: true }));
      try {
        const res = await axios.get(`${API_URL}/workshop-ratings/reviews/${workshopId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setReviews(prev => ({ ...prev, [workshopId]: res.data }));
      } catch (err) {
        setReviews(prev => ({ ...prev, [workshopId]: [] }));
      } finally {
        setLoadingReviews(prev => ({ ...prev, [workshopId]: false }));
      }
    }
  };

  // Defensive: filter out any "Unknown Workshop"
  const visibleRatings = ratings.filter(
    w => w.workshop && w.workshop !== 'Unknown Workshop'
  );

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      <h1 className="text-3xl font-bold text-blue-400 mb-4">⭐ Workshop Ratings</h1>
      {visibleRatings.length === 0 ? (
        <p className="text-gray-400">No ratings yet.</p>
      ) : (
        <ul className="space-y-4">
          {visibleRatings.map((w, idx) => (
            <li
              key={w._id || idx}
              className={`bg-gray-800 p-4 rounded shadow cursor-pointer transition-all duration-200
              ${expandedIdx === idx ? "ring-2 ring-blue-400" : "hover:bg-gray-700"}`}
              onClick={() => w.count > 0 && handleExpand(w._id, idx)}
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-lg font-semibold text-white flex items-center">
                    {w.workshop || w._id}
                    <span className="text-xs text-gray-400 ml-2">({w.count} reviews)</span>
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <StarRow value={w.averageRating || 0} />
                  {w.count > 0 && (expandedIdx === idx ? <FaChevronUp /> : <FaChevronDown />)}
                </div>
              </div>
              {/* Expanded Section: Show reviews */}
              {expandedIdx === idx && (
                <div className="mt-4 border-t border-gray-600 pt-2">
                  {loadingReviews[w._id] ? (
                    <span className="text-gray-400">Loading reviews...</span>
                  ) : reviews[w._id] && reviews[w._id].length === 0 ? (
                    <span className="text-gray-400">No reviews found.</span>
                  ) : (
                    <ul>
                      {reviews[w._id]?.map((rev, j) => (
                        <li key={j} className="mb-3 px-2 py-1 bg-gray-700 rounded">
                          <div className="flex items-center gap-2">
                            <span className="inline-block align-middle">
                              <StarRow value={rev.rating} size={14} />
                            </span>
                            {/* Compliments/tags as badges */}
                            {rev.compliments && rev.compliments.length > 0 && (
                              <div className="flex flex-wrap gap-1 ml-2">
                                {rev.compliments.map((tag, idx) => (
                                  <span key={idx} className="bg-blue-800 px-2 py-0.5 rounded text-xs">{tag}</span>
                                ))}
                              </div>
                            )}
                            {/* Show user name if available */}
                            {rev.user?.name && (
                              <span className="ml-2 text-xs text-green-300">{rev.user.name}</span>
                            )}
                            {/* Show comment/feedback if available */}
                            {(rev.comment || rev.feedback) && (
                              <span className="ml-2">{rev.comment || rev.feedback}</span>
                            )}
                            <span className="ml-2 text-xs text-gray-400">
                              {new Date(rev.updatedAt).toLocaleDateString()}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default WorkshopRatings;
